import { useEffect, useState, useMemo } from "react";
import api from "../api";

const STD_FIELDS = ["nome", "cognome", "azienda", "email"];
const SYSTEM_FIELDS = ["token", "link_registro"];

export default function Campaigns() {
  const [templates, setTemplates] = useState([]);
  const [senders, setSenders] = useState([]);
  const [tags, setTags] = useState([]);
  const [tplId, setTplId] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [mailboxId, setMailboxId] = useState("");
  const [recips, setRecips] = useState([]); // {email, nome, cognome, azienda, ...custom}
  const [tagId, setTagId] = useState("");
  const [kind, setKind] = useState("");
  const [incCompanies, setIncCompanies] = useState(true);
  const [incPersons, setIncPersons] = useState(true);
  const [manualEmail, setManualEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [progress, setProgress] = useState(null); // {total, sent, failed, status}
  const [campaignId, setCampaignId] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [respectConsent, setRespectConsent] = useState(true);
  const [lastSkipped, setLastSkipped] = useState(null);
  const [history, setHistory] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [previewIdx, setPreviewIdx] = useState(0);

  const loadHistory = () => api.get("/email-templates/campaigns", { noCache: true }).then((r) => setHistory(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/email-templates/senders").then((r) => { setSenders(r.data); const d = r.data.find((s) => s.default); if (d) setMailboxId(d.id); }).catch(() => {});
    api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
    loadHistory();
  }, []);

  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setSubject(t.subject || ""); setBodyHtml(t.body_html || ""); setBodyText(t.body_text || ""); }
  };

  // campi personalizzati = placeholder nel testo, esclusi quelli standard
  const customFields = useMemo(() => {
    const blob = `${subject} ${bodyText} ${bodyHtml}`;
    const found = [...blob.matchAll(/\{([^}{]+)\}/g)].map((m) => m[1].trim().toLowerCase());
    return [...new Set(found)].filter((f) => f && !STD_FIELDS.includes(f) && !SYSTEM_FIELDS.includes(f));
  }, [subject, bodyText, bodyHtml]);

  const previewFields = useMemo(() => {
    const r = recips[previewIdx] || recips[0];
    const base = r
      ? { email: r.email, nome: r.nome || "", cognome: r.cognome || "", azienda: r.azienda || "", ...Object.fromEntries(customFields.map((f) => [f, r[f] || ""])) }
      : { email: "mario.rossi@esempio.it", nome: "Mario", cognome: "Rossi", azienda: "Esempio Srl", ...Object.fromEntries(customFields.map((f) => [f, `[${f}]`])) };
    return { ...base, token: "", link_registro: "#" };
  }, [recips, previewIdx, customFields]);

  const renderPreview = (text) => {
    if (!text) return "";
    const repl = (m, key) => String(previewFields[key.trim().toLowerCase()] ?? "");
    return text.replace(/\{\{([^}{]+)\}\}/g, repl).replace(/\{([^}{]+)\}/g, repl);
  };

  const sendTest = async () => {
    const to = testTo.trim();
    if (!to) { setMsg("Indica l'email a cui mandare la prova."); return; }
    if (!subject.trim() && !bodyText.trim() && !bodyHtml.trim()) { setMsg("Scegli un template o scrivi oggetto e testo prima della prova."); return; }
    setTestBusy(true);
    try {
      await api.post("/email-templates/send-test", {
        to, subject, body_html: bodyHtml, body_text: bodyText,
        test_name: previewFields.nome || "Prova", base_url: window.location.origin, mailbox_id: mailboxId,
      });
      setMsg(`✅ Email di prova inviata a ${to} (mittente, link e tracking identici all'invio reale). Controlla la casella prima di lanciare la campagna.`);
    } catch (e) { setMsg("Errore prova: " + (e.response?.data?.detail || e.message)); }
    setTestBusy(false);
  };

  const addAudience = async () => {
    try {
      const p = new URLSearchParams();
      if (tagId) p.set("tag_id", tagId);
      if (kind) p.set("kind", kind);
      p.set("include_companies", incCompanies);
      p.set("include_persons", incPersons);
      const r = await api.get(`/email-templates/audience?${p.toString()}`);
      const existing = new Set(recips.map((x) => x.email));
      const add = r.data.filter((x) => !existing.has(x.email));
      setRecips([...recips, ...add]);
      setMsg(`Aggiunti ${add.length} destinatari (${r.data.length - add.length} già presenti).`);
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const addManual = () => {
    const em = manualEmail.trim().toLowerCase();
    if (!em || recips.some((x) => x.email === em)) { setManualEmail(""); return; }
    setRecips([...recips, { email: em, nome: "", cognome: "", azienda: "" }]);
    setManualEmail("");
  };
  const updRecip = (i, key, val) => setRecips(recips.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const delRecip = (i) => setRecips(recips.filter((_, idx) => idx !== i));
  const clearRecips = () => setRecips([]);

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/email-templates/import-recipients", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const existing = new Set(recips.map((x) => x.email));
      const add = r.data.recipients.filter((x) => !existing.has(x.email)).map((x) => ({ email: x.email, nome: x.nome, cognome: x.cognome, azienda: x.azienda, ...x.fields }));
      setRecips([...recips, ...add]);
      setMsg(`Importati ${add.length} destinatari dal file. Colonne rilevate: ${r.data.fields.join(", ") || "—"}.`);
    } catch (err) { setMsg("Errore import: " + (err.response?.data?.detail || err.message)); }
    e.target.value = "";
  };

  const send = async () => {
    if (!subject.trim()) { setMsg("Indica l'oggetto."); return; }
    if (recips.length === 0) { setMsg("Aggiungi almeno un destinatario."); return; }
    const recipients = recips.map((r) => ({
      email: r.email,
      fields: { nome: r.nome || "", cognome: r.cognome || "", azienda: r.azienda || "", ...Object.fromEntries(customFields.map((f) => [f, r[f] || ""])) },
    }));
    // anteprima consensi prima dell'invio
    let sendable = recips.length, skipped = 0;
    try {
      const prev = await api.post("/email-templates/consent-preview", { subject, recipients, respect_consent: respectConsent });
      sendable = prev.data.sendable; skipped = prev.data.skipped;
      setLastSkipped(prev.data);
    } catch { /* se l'anteprima fallisce, prosegue comunque (il backend filtra) */ }
    if (sendable === 0) {
      setMsg(`Nessun destinatario inviabile: tutti i ${recips.length} sono esclusi (disiscritti o senza consenso confermato).`);
      return;
    }
    const note = respectConsent
      ? `\n\nVerranno inviate ${sendable} email${skipped ? ` (${skipped} escluse: disiscritti o consenso non confermato)` : ""}.`
      : `\n\n${skipped ? `${skipped} disiscritti esclusi automaticamente. ` : ""}Attenzione: stai inviando IGNORANDO il consenso marketing (usa solo per comunicazioni di servizio).`;
    if (!window.confirm(`Inviare l'email (uno alla volta, nessuno vedrà gli altri)?${note}`)) return;
    try {
      const r = await api.post("/email-templates/campaign", { subject, body_html: bodyHtml, body_text: bodyText, mailbox_id: mailboxId, recipients, respect_consent: respectConsent });
      setMsg(`Invio avviato a ${r.data.total} destinatari${r.data.skipped ? ` (${r.data.skipped} esclusi)` : ""}…`);
      setCampaignId(r.data.campaign_id);
      poll(r.data.campaign_id);
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const loadTracking = async (cid) => {
    try {
      const r = await api.get(`/email-templates/campaign/${cid || campaignId}/tracking`, { noCache: true });
      setTracking(r.data);
    } catch (e) { setMsg("Errore report: " + (e.response?.data?.detail || e.message)); }
  };

  const poll = (cid) => {
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/email-templates/campaign/${cid}`);
        setProgress(r.data);
        if (r.data.status === "completata") { clearInterval(t); setMsg(`Invio completato: ${r.data.sent} inviate, ${r.data.failed} fallite.`); loadHistory(); }
      } catch { clearInterval(t); }
    }, 1500);
  };

  return (
    <div>
      <h1 className="page-title">Invio massivo</h1>
      <p className="page-sub">Invia un template a un gruppo di contatti. Ogni destinatario riceve la propria email (nessuno vede gli altri indirizzi) con i campi personalizzati.</p>

      <div className="grid-2" style={{ alignItems: "start", gap: 18 }}>
        <div className="panel">
          <div className="field"><label>Template</label>
            <select value={tplId} onChange={(e) => pickTemplate(e.target.value)} data-testid="campaign-template">
              <option value="">— scrivi manualmente o scegli —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Mittente</label>
            <select value={mailboxId} onChange={(e) => setMailboxId(e.target.value)} data-testid="campaign-sender">
              {senders.map((s) => <option key={s.id} value={s.id}>{s.address}{s.default ? " (predefinita)" : ""}</option>)}
            </select>
          </div>
          <div className="field"><label>Oggetto</label><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="campaign-subject" placeholder="es. Scadenza F24 di {azienda}" /></div>
          <div className="field"><label>Testo (usa {"{nome}"}, {"{azienda}"}, {"{importo}"}…)</label>
            <textarea rows={6} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="campaign-body" placeholder="Buongiorno {nome}, le ricordiamo la scadenza F24 di {importo} €…" />
          </div>
          <p className="muted" style={{ fontSize: 12 }}>Campi disponibili: {STD_FIELDS.map((f) => `{${f}}`).join(", ")}{customFields.length > 0 && <> · personalizzati rilevati: <strong>{customFields.map((f) => `{${f}}`).join(", ")}</strong></>}</p>
        </div>

        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Destinatari ({recips.length})</h3>
          <div className="field"><label>Da etichetta / tipo</label>
            <div className="toolbar" style={{ gap: 6, flexWrap: "wrap" }}>
              <select value={tagId} onChange={(e) => setTagId(e.target.value)} data-testid="campaign-tag" style={{ maxWidth: 200 }}>
                <option value="">tutte le etichette</option>
                {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="campaign-kind" style={{ maxWidth: 140 }}>
                <option value="">clienti + lead</option>
                <option value="cliente">solo clienti</option>
                <option value="lead">solo lead</option>
              </select>
            </div>
            <div className="toolbar" style={{ gap: 10, marginTop: 6 }}>
              <label className="muted" style={{ display: "flex", gap: 4, alignItems: "center" }}><input type="checkbox" checked={incCompanies} onChange={(e) => setIncCompanies(e.target.checked)} /> aziende</label>
              <label className="muted" style={{ display: "flex", gap: 4, alignItems: "center" }}><input type="checkbox" checked={incPersons} onChange={(e) => setIncPersons(e.target.checked)} /> persone</label>
              <button className="btn sm primary" onClick={addAudience} data-testid="campaign-add-audience">+ Aggiungi gruppo</button>
            </div>
          </div>
          <div className="field"><label>Aggiungi manualmente</label>
            <div className="toolbar" style={{ gap: 6 }}>
              <input value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="email@esempio.it" data-testid="campaign-manual-email" onKeyDown={(e) => e.key === "Enter" && addManual()} />
              <button className="btn sm" onClick={addManual} data-testid="campaign-add-manual">+</button>
            </div>
          </div>
          <div className="field"><label>Importa da Excel/CSV (per F24: colonna email + importo)</label>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={importFile} data-testid="campaign-import-file" />
          </div>
          {recips.length > 0 && <button className="btn sm danger" onClick={clearRecips}>Svuota lista</button>}
        </div>
      </div>

      {recips.length > 0 && (
        <div className="panel" style={{ marginTop: 18 }}>
          <h3>Anteprima destinatari e campi</h3>
          <div style={{ maxHeight: 360, overflow: "auto" }}>
            <table>
              <thead><tr><th>Email</th><th>Nome</th><th>Cognome</th><th>Azienda</th>{customFields.map((f) => <th key={f}>{f}</th>)}<th></th></tr></thead>
              <tbody>
                {recips.map((r, i) => (
                  <tr key={i} data-testid={`campaign-recip-${i}`}>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{r.email}</td>
                    <td><input value={r.nome || ""} onChange={(e) => updRecip(i, "nome", e.target.value)} style={{ minWidth: 90 }} /></td>
                    <td><input value={r.cognome || ""} onChange={(e) => updRecip(i, "cognome", e.target.value)} style={{ minWidth: 90 }} /></td>
                    <td><input value={r.azienda || ""} onChange={(e) => updRecip(i, "azienda", e.target.value)} style={{ minWidth: 110 }} /></td>
                    {customFields.map((f) => <td key={f}><input value={r[f] || ""} onChange={(e) => updRecip(i, f, e.target.value)} placeholder={f} style={{ minWidth: 80 }} data-testid={`campaign-field-${f}-${i}`} /></td>)}
                    <td><button className="btn sm danger" onClick={() => delRecip(i)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(bodyHtml || bodyText || subject) && (
        <div className="panel" style={{ marginTop: 18 }} data-testid="campaign-preview-panel">
          <div className="toolbar" style={{ flexWrap: "wrap", gap: 10 }}>
            <h3 style={{ margin: 0 }}>👁 Anteprima email</h3>
            {recips.length > 0 && (
              <select value={previewIdx} onChange={(e) => setPreviewIdx(Number(e.target.value))} data-testid="campaign-preview-recipient" style={{ maxWidth: 260 }}>
                {recips.map((r, i) => <option key={i} value={i}>{r.email}</option>)}
              </select>
            )}
            <span className="muted" style={{ fontSize: 12 }}>
              {recips.length > 0 ? "coi campi del destinatario selezionato" : "con dati di esempio (Mario Rossi) — aggiungi destinatari per vedere i campi reali"}
            </span>
            <div className="spacer" />
            <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="tua email per la prova" data-testid="campaign-test-email" style={{ maxWidth: 240 }} />
            <button className="btn" onClick={sendTest} disabled={testBusy} data-testid="campaign-test-send">{testBusy ? "Invio…" : "✉️ Invia prova a me"}</button>
          </div>
          <p style={{ margin: "10px 0 6px" }}><strong>Oggetto:</strong> {renderPreview(subject) || <span className="muted">—</span>}</p>
          {bodyHtml ? (
            <iframe title="anteprima-campagna" srcDoc={renderPreview(bodyHtml)} data-testid="campaign-preview-iframe"
              style={{ width: "100%", height: 460, border: "1px solid rgba(255,255,255,.12)", borderRadius: 10, background: "#fff" }} />
          ) : (
            <pre data-testid="campaign-preview-text" style={{ whiteSpace: "pre-wrap", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, padding: 14, fontFamily: "inherit", fontSize: 14 }}>{renderPreview(bodyText) || "—"}</pre>
          )}
          <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
            La prova arriva dal mittente selezionato, con link tracciati e pixel identici alla campagna reale. Ricorda: l'invio massivo NON passa dalla coda «Da approvare» — parte subito dopo la conferma.
          </p>
        </div>
      )}

      <div className="panel" style={{ marginTop: 18 }}>
        <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }} data-testid="campaign-respect-consent-label">
          <input type="checkbox" checked={respectConsent} onChange={(e) => setRespectConsent(e.target.checked)} data-testid="campaign-respect-consent" />
          <span>Rispetta i consensi GDPR — invia <strong>solo ai contatti con opt-in confermato</strong> (i disiscritti sono sempre esclusi). Disattiva solo per comunicazioni di servizio (es. F24).</span>
        </label>
        <div className="toolbar">
          <button className="btn primary" onClick={send} disabled={progress?.status === "in_corso"} data-testid="campaign-send">Invia a {recips.length} destinatari</button>
          {msg && <span className="muted" data-testid="campaign-msg">{msg}</span>}
        </div>
        {lastSkipped && lastSkipped.skipped > 0 && (
          <p className="muted" data-testid="campaign-skipped" style={{ marginTop: 8, color: "#b45309" }}>
            {lastSkipped.skipped} destinatari esclusi su {lastSkipped.total} (disiscritti o consenso non confermato).
          </p>
        )}
        {progress && (
          <div style={{ marginTop: 10 }} data-testid="campaign-progress">
            <div style={{ height: 10, background: "rgba(255,255,255,.08)", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round(((progress.sent + progress.failed) / Math.max(progress.total, 1)) * 100)}%`, background: "var(--grad)" }} />
            </div>
            <p className="muted" style={{ marginTop: 6 }}>{progress.sent + progress.failed}/{progress.total} · inviate {progress.sent}, fallite {progress.failed} · {progress.status}</p>
          </div>
        )}
        {campaignId && (
          <div style={{ marginTop: 14 }} data-testid="campaign-tracking-section">
            <div className="toolbar">
              <button className="btn sm" onClick={() => loadTracking()} data-testid="campaign-tracking-btn">📊 Aggiorna report aperture e download</button>
              {tracking && (
                <span className="muted" data-testid="campaign-tracking-summary">
                  Inviate {tracking.summary.sent} · Aperte {tracking.summary.opened} · Lette fino in fondo {tracking.summary.read_bottom} · Link cliccati {tracking.summary.clicked ?? 0} · File scaricato {tracking.summary.downloaded}
                </span>
              )}
            </div>
            {tracking && tracking.rows.length > 0 && (
              <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 8 }}>
                <table style={{ fontSize: 13 }} data-testid="campaign-tracking-table">
                  <thead><tr><th>Destinatario</th><th>Aperta</th><th>Letta fino in fondo</th><th>Link cliccato</th><th>File scaricato</th></tr></thead>
                  <tbody>
                    {tracking.rows.map((r) => {
                      const dt = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
                      return (
                        <tr key={r.email}>
                          <td>{r.email}</td>
                          <td style={{ color: r.opened_at ? "#6ee7b7" : "inherit" }}>{r.opened_at ? `✓ ${dt(r.opened_at)}` : "—"}</td>
                          <td style={{ color: r.bottom_at ? "#6ee7b7" : "inherit" }}>{r.bottom_at ? `✓ ${dt(r.bottom_at)}` : "—"}</td>
                          <td style={{ color: r.clicked_at ? "#6ee7b7" : "inherit" }} title={(r.clicked_urls || []).join("\n")}>{r.clicked_at ? `✓ ${dt(r.clicked_at)}` : "—"}</td>
                          <td style={{ color: r.downloaded_at ? "#6ee7b7" : "inherit" }}>{r.downloaded_at ? `✓ ${dt(r.downloaded_at)}` : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>Nota: l'apertura si rileva solo se il destinatario carica le immagini; "letta fino in fondo" è una stima. Chi elimina l'email senza aprirla non è tecnicamente tracciabile: consideralo "non aperta".</p>
          </div>
        )}
      </div>

      <div className="panel" style={{ marginTop: 18 }} data-testid="campaign-history">
        <div className="toolbar">
          <h3 style={{ margin: 0 }}>Storico campagne</h3>
          <div className="spacer" />
          <button className="btn sm" onClick={loadHistory} data-testid="campaign-history-refresh">↻ Aggiorna</button>
        </div>
        {history.length === 0 ? (
          <div className="empty">Nessuna campagna inviata finora.</div>
        ) : (
          <table style={{ fontSize: 13, marginTop: 8 }} data-testid="campaign-history-table">
            <thead><tr><th>Data</th><th>Oggetto</th><th>Inviate</th><th>Fallite</th><th>Escluse</th><th>Stato</th><th></th></tr></thead>
            <tbody>
              {history.map((c) => (
                <tr key={c.id} style={{ background: c.id === campaignId ? "rgba(255,255,255,.05)" : "transparent" }}>
                  <td style={{ whiteSpace: "nowrap" }}>{c.created_at ? new Date(c.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td>{c.subject || "—"}</td>
                  <td>{c.sent}/{c.total}</td>
                  <td style={{ color: c.failed ? "#f87171" : "inherit" }}>{c.failed}</td>
                  <td>{c.skipped}</td>
                  <td>{c.status === "completata" ? "✓ completata" : c.status}</td>
                  <td><button className="btn sm" onClick={() => { setCampaignId(c.id); setProgress(null); loadTracking(c.id); }} data-testid={`campaign-history-report-${c.id}`}>📊 Report</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
